import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Switch,
    Dialog,
    DialogHeader,
    DialogBody,
    IconButton,
} from "@material-tailwind/react";

import {
    PencilIcon,
    TrashIcon,
    ArrowPathIcon,
} from "@heroicons/react/24/outline";

import { useDispatch, useSelector } from "react-redux";

import {
    getCurrencies,
    saveCurrency,
    deleteCurrency,
    setDefaultCurrency,
    syncCurrencyRates,
} from "@/store/slices/currencySlice";

import CurrencyForm from "./CurrencyForm";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";

const getFlag = (countryCode = "") => {
    if (!countryCode || countryCode.length !== 2) {
        return "";
    }

    return countryCode
        .toUpperCase()
        .split("")
        .map((char) =>
            String.fromCodePoint(
                127397 + char.charCodeAt(0)
            )
        )
        .join("");
};

export default function CurrencyManagement() {
    const { t } = useTranslation("currency");

    const dispatch = useDispatch();

    /*
    |--------------------------------------------------------------------------
    | Redux State
    |--------------------------------------------------------------------------
    */

    const {
        list_currency,
        pagination,
        list_loading,
        loading,
        delete_loading,
        default_loading,
        sync_loading,
        error,
    } = useSelector(
        (state) => state.currency
    );

    /*
    |--------------------------------------------------------------------------
    | Local State
    |--------------------------------------------------------------------------
    */

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);

    const [perPage, setPerPage] = useState(20);

    const [open, setOpen] = useState(false);

    const [editingCurrency, setEditingCurrency] =
        useState(null);

    /*
    |--------------------------------------------------------------------------
    | Load Currencies
    |--------------------------------------------------------------------------
    */

    const loadCurrencies = () => {

        dispatch(
            getCurrencies({
                search,
                perPage,
                page,
            })
        );
    };

    /*
    |--------------------------------------------------------------------------
    | Initial Load / Search / Pagination
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        loadCurrencies();

    }, [
        dispatch,
        search,
        page,
        perPage,
    ]);

    /*
    |--------------------------------------------------------------------------
    | Open Create Dialog
    |--------------------------------------------------------------------------
    */

    const handleOpenCreate = () => {

        setEditingCurrency(null);

        setOpen(true);
    };

    /*
    |--------------------------------------------------------------------------
    | Open Edit Dialog
    |--------------------------------------------------------------------------
    */

    const handleEdit = (currency) => {

        setEditingCurrency(currency);

        setOpen(true);
    };

    /*
    |--------------------------------------------------------------------------
    | Close Dialog
    |--------------------------------------------------------------------------
    */

    const handleClose = () => {

        if (loading) {
            return;
        }

        setOpen(false);

        setEditingCurrency(null);
    };

    /*
    |--------------------------------------------------------------------------
    | Save Currency
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (data) => {

        try {

            await dispatch(
                saveCurrency({
                    ...data,
                    ...(editingCurrency
                        ? {
                            id: editingCurrency.id,
                        }
                        : {}),
                })
            ).unwrap();

            setOpen(false);

            setEditingCurrency(null);
            Toaster.success(t('updated_successfully'))

            loadCurrencies();

        } catch (error) {
            Toaster.error(error?.message);
            console.error(
                "Save currency error:",
                error
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Delete Currency
    |--------------------------------------------------------------------------
    */

    const handleDelete = async (currency) => {
        if (currency.is_default) {
            Swal.fire({
                icon: t('warning'),
                title: t('cannot_delete'),
                text: t('default_currency_cannot_be_deleted'),
            });

            return;
        }

        const result = await Swal.fire({
            title: t('are_you_sure'),
            text: `You are about to delete ${currency.currency_code}.`,
            icon: t('warning'),
            showCancelButton: true,
            confirmButtonText: t('yes_delete_it'),
            cancelButtonText: t('cancel'),
            reverseButtons: true,
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await dispatch(
                deleteCurrency(currency.id)
            ).unwrap();

            await Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: `${currency.currency_code} has been deleted successfully.`,
                timer: 1500,
                showConfirmButton: false,
            });

            loadCurrencies();

        } catch (error) {
            console.error(
                "Delete currency error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text:
                    error?.message ||
                    "Failed to delete currency.",
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Set Default Currency
    |--------------------------------------------------------------------------
    */

    const handleDefault = async (currency) => {
        if (currency.is_default) {
            return;
        }

        const result = await Swal.fire({
            title: "Set Default Currency?",
            text: `${currency.currency_code} will become the default currency.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, set as default",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await dispatch(
                setDefaultCurrency(currency.id)
            ).unwrap();

            await Swal.fire({
                icon: "success",
                title: "Default Currency Updated",
                text: `${currency.currency_code} is now the default currency.`,
                timer: 1500,
                showConfirmButton: false,
            });

            loadCurrencies();

        } catch (error) {
            console.error(
                "Set default currency error:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Failed",
                text:
                    error?.message ||
                    "Failed to set default currency.",
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Sync Currency Rates
    |--------------------------------------------------------------------------
    */

    const handleSync = async () => {

        try {

            await dispatch(
                syncCurrencyRates()
            ).unwrap();

            /*
             * Reload the currencies so the
             * updated exchange rates are shown.
             */

            loadCurrencies();

        } catch (error) {

            console.error(
                "Currency sync error:",
                error
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const handleSearch = (e) => {

        setSearch(e.target.value);

        /*
         * When searching, start again
         * from first page.
         */

        setPage(1);
    };

    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (
        <div className="p-6">

            <Card>

                <CardBody>

                    <div className="flex flex-col gap-5">

                        {/* =====================================================
                            Header
                        ====================================================== */}

                        <div
                            className="
                                flex
                                flex-col
                                gap-4
                                md:flex-row
                                md:items-center
                                md:justify-between
                            "
                        >

                            <div>

                                <Typography
                                    variant="h5"
                                >
                                     {t('currency_country_management')}
                                </Typography>

                                <Typography
                                    color="gray"
                                    className="mt-1"
                                >
                                    {t('manage_supported_countries_currencies_exchange_rates')}
                                </Typography>

                            </div>

                            <div className="flex gap-2">

                                {/* Sync */}

                                <Button
                                    variant="outlined"
                                    onClick={handleSync}
                                    disabled={
                                        sync_loading
                                    }
                                    className="
                                        flex
                                        items-center
                                        gap-2
                                    "
                                >

                                    <ArrowPathIcon
                                        className={`
                                            h-5
                                            w-5
                                            ${
                                                sync_loading
                                                    ? "animate-spin"
                                                    : ""
                                            }
                                        `}
                                    />

                                    {sync_loading
                                        ? t('syncing')
                                        : t('sync_rates')
                                    }

                                </Button>

                                {/* Add */}

                                <Button
                                    onClick={
                                        handleOpenCreate
                                    }
                                >
                                    {t('add_currency')}
                                </Button>

                            </div>

                        </div>


                        {/* =====================================================
                            Error
                        ====================================================== */}

                        {error && (
                            <div
                                className="
                                    rounded-lg
                                    bg-red-50
                                    px-4
                                    py-3
                                    text-sm
                                    text-red-600
                                "
                            >
                                {typeof error === "string"
                                    ? error
                                    : error?.message ||
                                      "Something went wrong."
                                }
                            </div>
                        )}


                        {/* =====================================================
                            Search
                        ====================================================== */}

                        <div className="max-w-md">

                            <Input
                                label={t('search_country_currency')}
                                value={search}
                                onChange={
                                    handleSearch
                                }
                            />

                        </div>


                        {/* =====================================================
                            Table
                        ====================================================== */}

                        <div className="relative overflow-x-auto">

                            {list_loading && (
                                <div
                                    className="
                                        absolute
                                        inset-0
                                        z-10
                                        flex
                                        items-center
                                        justify-center
                                        bg-white/70
                                    "
                                >

                                    <Typography>
                                        {t('loading_currencies')}
                                    </Typography>

                                </div>
                            )}

                            <table
                                className="
                                    w-full
                                    min-w-max
                                    table-auto
                                    text-left
                                "
                            >

                                <thead>

                                    <tr>

                                        <th
                                            className="
                                                border-b
                                                border-blue-gray-100
                                                bg-blue-gray-50/50
                                                p-4
                                            "
                                        >
                                            <Typography
                                                variant="small"
                                                className="
                                                    font-normal
                                                    leading-none
                                                    opacity-70
                                                "
                                            >
                                                {t('country')}
                                            </Typography>
                                        </th>

                                        <th
                                            className="
                                                border-b
                                                border-blue-gray-100
                                                bg-blue-gray-50/50
                                                p-4
                                            "
                                        >
                                            <Typography
                                                variant="small"
                                                className="
                                                    font-normal
                                                    leading-none
                                                    opacity-70
                                                "
                                            >
                                                {t('currency')}
                                            </Typography>
                                        </th>

                                        <th
                                            className="
                                                border-b
                                                border-blue-gray-100
                                                bg-blue-gray-50/50
                                                p-4
                                            "
                                        >
                                            <Typography
                                                variant="small"
                                                className="
                                                    font-normal
                                                    leading-none
                                                    opacity-70
                                                "
                                            >
                                                {t('exchange_rate')}
                                            </Typography>
                                        </th>

                                        <th
                                            className="
                                                border-b
                                                border-blue-gray-100
                                                bg-blue-gray-50/50
                                                p-4
                                                text-center
                                            "
                                        >
                                            <Typography
                                                variant="small"
                                                className="
                                                    font-normal
                                                    leading-none
                                                    opacity-70
                                                "
                                            >
                                                {t('status')}
                                            </Typography>
                                        </th>

                                        <th
                                            className="
                                                border-b
                                                border-blue-gray-100
                                                bg-blue-gray-50/50
                                                p-4
                                                text-center
                                            "
                                        >
                                            <Typography
                                                variant="small"
                                                className="
                                                    font-normal
                                                    leading-none
                                                    opacity-70
                                                "
                                            >
                                                {t('default')}
                                            </Typography>
                                        </th>

                                        <th
                                            className="
                                                border-b
                                                border-blue-gray-100
                                                bg-blue-gray-50/50
                                                p-4
                                                text-center
                                            "
                                        >
                                            <Typography
                                                variant="small"
                                                className="
                                                    font-normal
                                                    leading-none
                                                    opacity-70
                                                "
                                            >
                                                {t('actions')}
                                            </Typography>
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {!list_loading &&
                                    list_currency.length ===
                                        0 ? (

                                        <tr>

                                            <td
                                                colSpan="6"
                                                className="p-8 text-center"
                                            >

                                                <Typography
                                                    color="gray"
                                                >
                                                   {t('no_currencies_found')}
                                                </Typography>

                                            </td>

                                        </tr>

                                    ) : (

                                        list_currency.map(
                                            (
                                                currency
                                            ) => (

                                                <tr
                                                    key={
                                                        currency.id
                                                    }
                                                    className="
                                                        hover:bg-blue-gray-50/30
                                                    "
                                                >

                                                    {/* Country */}

                                                    <td
                                                        className="
                                                            border-b
                                                            border-blue-gray-50
                                                            p-4
                                                        "
                                                    >

                                                        <div
                                                            className="
                                                                flex
                                                                items-center
                                                                gap-3
                                                            "
                                                        >

                                                            <span
                                                                className="
                                                                    text-2xl
                                                                "
                                                            >
                                                                {getFlag(
                                                                    currency.country_code
                                                                )}
                                                            </span>

                                                            <div>

                                                                <Typography
                                                                    variant="small"
                                                                    className="font-medium"
                                                                >
                                                                    {
                                                                        currency.country_name
                                                                    }
                                                                </Typography>

                                                                <Typography
                                                                    variant="small"
                                                                    color="gray"
                                                                >
                                                                    {
                                                                        currency.country_code
                                                                    }
                                                                </Typography>

                                                            </div>

                                                        </div>

                                                    </td>


                                                    {/* Currency */}

                                                    <td
                                                        className="
                                                            border-b
                                                            border-blue-gray-50
                                                            p-4
                                                        "
                                                    >

                                                        <div>

                                                            <Typography
                                                                variant="small"
                                                                className="font-medium"
                                                            >
                                                                {
                                                                    currency.currency_name
                                                                }
                                                            </Typography>

                                                            <Typography
                                                                variant="small"
                                                                color="gray"
                                                            >

                                                                {
                                                                    currency.currency_code
                                                                }

                                                                {" "}

                                                                {
                                                                    currency.currency_symbol
                                                                }

                                                            </Typography>

                                                        </div>

                                                    </td>


                                                    {/* Exchange Rate */}

                                                    <td
                                                        className="
                                                            border-b
                                                            border-blue-gray-50
                                                            p-4
                                                        "
                                                    >

                                                        <Typography
                                                            variant="small"
                                                        >
                                                            {Number(
                                                                currency.exchange_rate
                                                            ).toFixed(
                                                                6
                                                            )}
                                                        </Typography>

                                                        {currency.is_manual_rate && (
                                                            <Typography
                                                                variant="small"
                                                                color="orange"
                                                            >
                                                                Manual
                                                            </Typography>
                                                        )}

                                                    </td>


                                                    {/* Status */}

                                                   <td className="border-b border-blue-gray-50 p-4 align-middle">
                                                        <div className="flex items-center justify-center">
                                                            <Switch
                                                                checked={Boolean(currency.is_active)}
                                                                disabled={currency.is_default}
                                                                onChange={() => handleEdit(currency)}
                                                            />
                                                        </div>
                                                    </td>


                                                    {/* Default */}

                                                    <td
                                                        className="
                                                            border-b
                                                            border-blue-gray-50
                                                            p-4
                                                            text-center
                                                        "
                                                    >

                                                        <input
                                                            type="radio"
                                                            name="default_currency"
                                                            checked={
                                                                Boolean(
                                                                    currency.is_default
                                                                )
                                                            }
                                                            disabled={
                                                                default_loading ||
                                                                !currency.is_active
                                                            }
                                                            onChange={() =>
                                                                handleDefault(
                                                                    currency
                                                                )
                                                            }
                                                            className="
                                                                h-4
                                                                w-4
                                                                cursor-pointer
                                                            "
                                                        />

                                                    </td>


                                                    {/* Actions */}

                                                   <td className="border-b border-blue-gray-50 p-4 align-middle">
                                                        {/* Use a 2-column grid, centered in the cell */}
                                                        <div className="grid grid-cols-2 gap-2 place-items-center w-max mx-auto">
                                                            
                                                            {/* Column 1: Edit (Always present) */}
                                                            <IconButton
                                                                variant="text"
                                                                onClick={() => handleEdit(currency)}
                                                                disabled={loading || delete_loading}
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                            </IconButton>

                                                            {/* Column 2: Delete (or an empty space to hold the layout) */}
                                                            {/* {!currency.is_default ? (
                                                                <IconButton
                                                                    variant="text"
                                                                    color="red"
                                                                    onClick={() => handleDelete(currency)}
                                                                    disabled={delete_loading}
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </IconButton>
                                                            ) : (
                                                                 <div className="w-10 h-10"></div> 
                                                            )} */}

                                                        </div>
                                                    </td>
                                                </tr>

                                            )
                                        )

                                    )}

                                </tbody>

                            </table>

                        </div>


                        {/* =====================================================
                            Pagination
                        ====================================================== */}

                        {pagination &&
                            pagination.last_page >
                                1 && (

                                <div
                                    className="
                                        flex
                                        items-center
                                        justify-between
                                        border-t
                                        pt-4
                                    "
                                >

                                    <Typography
                                        variant="small"
                                        color="gray"
                                    >
                                        Page{" "}
                                        {
                                            pagination.current_page
                                        }{" "}
                                        of{" "}
                                        {
                                            pagination.last_page
                                        }
                                    </Typography>

                                    <div className="flex gap-2">

                                        <Button
                                            variant="outlined"
                                            size="sm"
                                            disabled={
                                                pagination.current_page <=
                                                1
                                            }
                                            onClick={() =>
                                                setPage(
                                                    (prev) =>
                                                        Math.max(
                                                            1,
                                                            prev -
                                                                1
                                                        )
                                                )
                                            }
                                        >
                                            Previous
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            size="sm"
                                            disabled={
                                                pagination.current_page >=
                                                pagination.last_page
                                            }
                                            onClick={() =>
                                                setPage(
                                                    (prev) =>
                                                        Math.min(
                                                            pagination.last_page,
                                                            prev +
                                                                1
                                                        )
                                                )
                                            }
                                        >
                                            Next
                                        </Button>

                                    </div>

                                </div>

                            )}

                    </div>

                </CardBody>

            </Card>


            {/* ================================================================
                Currency Form Dialog
            ================================================================= */}

            <Dialog
                open={open}
                handler={handleClose}
                size="lg"
            >

                <DialogHeader>
                    {editingCurrency
                        ? "Edit Currency"
                        : "Add Currency"
                    }
                </DialogHeader>

                <DialogBody
                    className="
                        max-h-[75vh]
                        overflow-y-auto
                    "
                >

                    <CurrencyForm
                        currency={
                            editingCurrency
                        }
                        loading={loading}
                        onCancel={handleClose}
                        onSubmit={handleSubmit}
                    />

                </DialogBody>

            </Dialog>

        </div>
    );
}